import { cn } from '@ordo/ui/cn'

import { SERVE_DAY_BADGE_CLASS, serveDayBadges } from '@/lib/chefs/serve-day'
import type { Mealtime } from '@/lib/chefs/types'

interface MealtimeHeaderProps {
  mealtime: Mealtime
  /**
   * True meal count ("330 Meals"). Omitted = no count: the chef Orders screen
   * keeps only the mealtime and the serve day (PROD-1502, Abner's note on
   * Figma 164:873); print and the other surfaces still pass it.
   */
  count?: number
  /**
   * SERVICE days this block feeds (`YYYY-MM-DD`) — renders the
   * "Serve Today" / "Serve Mon" badge(s) after the count. Omitted = no badge;
   * a heading must never guess a serve day it wasn't given.
   */
  serveDays?: readonly string[]
  /** Pin "today" for deterministic rendering (print, tests). */
  todayKey?: string
}

const MEALTIME_LABEL: Record<Mealtime, string> = {
  breakfast: 'Breakfast',
  'am-snack': 'AM Snack',
  lunch: 'Lunch',
  'pm-snack': 'PM Snack',
  supper: 'Supper',
}

export function MealtimeHeader({ mealtime, count, serveDays, todayKey }: MealtimeHeaderProps) {
  const label = MEALTIME_LABEL[mealtime]
  const badges = serveDayBadges(serveDays, todayKey)

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-t-[10px] border-b border-border bg-card px-4 py-3 text-sm font-medium">
      <span className="text-foreground">{label}</span>
      {count !== undefined ? (
        <>
          <span className="text-muted-foreground">·</span>
          <span className="text-muted-foreground">{count} Meals</span>
        </>
      ) : null}
      {/* Two badges on one heading = the deliberate food-safety split: a
          same-day menu and a day-before menu sharing this campus + mealtime
          on one delivery day. Both are real; don't collapse them. */}
      {badges.map((badge) => (
        <span
          key={badge.label}
          className={cn(
            'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold',
            SERVE_DAY_BADGE_CLASS[badge.tone],
          )}>
          {badge.label}
        </span>
      ))}
    </div>
  )
}
